function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

// PF perf 2026-10-29: run cb once el is within `margin` of the viewport (below-fold slider init off the load path).
window.pfWhenNear = function (el, cb, margin) {
  if (!el || !('IntersectionObserver' in window)) return cb();
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    io.disconnect();
    cb();
  }, { rootMargin: margin || '800px 0px' });
  io.observe(el);
};

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function pfInitSummaries(root) {
root.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});
}
pfInitSummaries(document);

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();

    // PF perf 2026-10-29: header-drawer.liquid ships the drawer in <template data-pf-drawer> (1,100+ elements out of the live DOM);
    // It is stamped in idle time after load (review B1 2026-10-29: stamping inside the first tap made that tap slower than
    // live), so the first menu tap finds it already there; the hamburger intent and the click itself are fallbacks only.
    const mainSummary = this.mainDetailsToggle && this.mainDetailsToggle.querySelector('summary');
    if (mainSummary && this.querySelector('template[data-pf-drawer]')) {
      const hydrate = () => this.pfHydrate();
      ['pointerdown', 'touchstart', 'focusin', 'keydown', 'mouseenter'].forEach((type) =>
        mainSummary.addEventListener(type, hydrate, { once: true, passive: true })
      );
      const idle = () =>
        'requestIdleCallback' in window ? requestIdleCallback(hydrate, { timeout: 3000 }) : setTimeout(hydrate, 1500);
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    }
  }

  pfHydrate() {
    const tpl = this.querySelector('template[data-pf-drawer]');
    if (!tpl) return;
    const t0 = performance.now();
    const roots = Array.from(tpl.content.children);
    tpl.replaceWith(tpl.content);
    roots.forEach((root) => {
      this.bindEvents(root);
      pfInitSummaries(root);
    });
    try { performance.measure('pf:drawer-stamp', { start: t0, end: performance.now() }); } catch (e) {}
  }

  bindEvents(root = this) {
    root.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    root.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    root.querySelectorAll('.close-button').forEach((button) => {
      button.addEventListener(
        'click',
        this.closeFromCustomButton.bind(this)
      );
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      this.pfHydrate();
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeFromCustomButton(event) {
    event.preventDefault();

    const details = event.currentTarget.closest('details');
    if (!details) return;

    const summary = details.querySelector('summary');
    if (!summary) return;

    // Delegate to the same close logic Shopify already uses
    summary.click();
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    if (this.constructor !== SliderComponent) this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton || !this.sliderItemsToShow) return;

    const previousPage = this.currentPage;
    // PSI 2026-10-30 (forced reflow): read every layout value BEFORE the first write below. The counter
    // textContent and the button attributes are DOM writes; reading offsetLeft/clientWidth after them
    // (isSlideVisible) made the browser lay the page out again synchronously on every scroll and resize.
    const scrollLeft = this.slider.scrollLeft;
    this.currentPage = Math.round(scrollLeft / this.sliderItemOffset) + 1;
    const firstSlideVisible = !this.enableSliderLooping && this.isSlideVisible(this.sliderItemsToShow[0]) && scrollLeft === 0;
    const lastSlideVisible =
      !this.enableSliderLooping && this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]);

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (firstSlideVisible) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (lastSlideVisible) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// custom edition

  if (!customElements.get('featured-collection-carousel')) {
    class FeaturedCollectionCarousel extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.swiperInstance = null;
      }

      connectedCallback() {
        // Initialize when element is connected to DOM
        this.initSwiper();
      }

      disconnectedCallback() {
        // Cleanup when element is removed from DOM
        this.destroySwiper();
        if (this.pfDesktopQuery) {
          this.pfDesktopQuery.removeEventListener('change', this.pfOnBreakpoint);
          this.pfDesktopQuery = null;
        }
      }

      init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.initSwiper());
        } else {
          this.initSwiper();
        }
      }

      initSwiper() {
        const section = this.closest('.featured-collection-section');
        if (!section) return;

        const desktopLayout = section.dataset.desktopLayout;
        const mobileLayout = section.dataset.mobileLayout;

        // Check if Swiper is needed based on layout settings
        const needsSwiper = mobileLayout === 'carousel' || desktopLayout === 'carousel';

        if (!needsSwiper) {
          return;
        }

        // PF 2026-11-02: swiper-bundle.min.js is deferred and loads after this file (theme.liquid), so on first connect
        // Swiper is undefined; the old early return here never retried, and every phone carousel sat frozen on card 1
        // with no swipe. Retry once the deferred scripts have run, as collection-slider and testimonials-slider do.
        if (typeof Swiper === 'undefined') {
          if (!this.pfSwiperRetry) {
            this.pfSwiperRetry = () => {
              if (!this.swiperInstance && typeof Swiper !== 'undefined') this.initSwiper();
            };
            document.addEventListener('DOMContentLoaded', this.pfSwiperRetry, { once: true });
            window.addEventListener('load', this.pfSwiperRetry, { once: true });
          }
          return;
        }

        // PF 2026-11-02: build Swiper only at the widths whose layout is a carousel. A disabled Swiper still writes
        // inline slide widths and margins, which narrowed the desktop grid cards (357 -> 333 px at 1920); crossing
        // 750 px rebuilds or tears it down.
        if (!this.pfDesktopQuery) {
          this.pfDesktopQuery = window.matchMedia('(min-width: 750px)');
          this.pfOnBreakpoint = () => this.initSwiper();
          this.pfDesktopQuery.addEventListener('change', this.pfOnBreakpoint);
        }
        if ((this.pfDesktopQuery.matches ? desktopLayout : mobileLayout) !== 'carousel') {
          this.destroySwiper();
          return;
        }

        // Destroy existing instance if it exists
        this.destroySwiper();

        const swiperElement = this.querySelector('.featured-collection-swiper');
        const swiperWrapper = swiperElement.querySelector('.swiper-wrapper');
        
        if (!swiperElement || !swiperWrapper) return;

        // Reset grid styles for Swiper
        swiperWrapper.style.display = '';
        swiperWrapper.style.gridTemplateColumns = '';
        swiperWrapper.style.gap = '';

        // Determine configuration based on screen size and settings
        const isDesktopCarousel = desktopLayout === 'carousel';
        const isMobileCarousel = mobileLayout === 'carousel';

        const swiperConfig = {
          slidesPerView: isMobileCarousel ? 1.15 : 1,
          spaceBetween: 20,
          enabled: isMobileCarousel, // Enable on mobile only if carousel
          breakpoints: {
            750: {
              enabled: isDesktopCarousel, // Enable on desktop only if carousel
              slidesPerView: isDesktopCarousel ? 3.2 : 4,
              spaceBetween: 24,
            },
            990: {
              enabled: isDesktopCarousel,
              slidesPerView: isDesktopCarousel ? 3.5 : 4,
              spaceBetween: 24,
            },
            1200: {
              enabled: isDesktopCarousel,
              slidesPerView: 4,
              spaceBetween: 24,
            },
          },
        };

        // Add scrollbar if setting is enabled and mobile is carousel
        const scrollbarElement = swiperElement.querySelector('.swiper-scrollbar');
        if (scrollbarElement && isMobileCarousel) {
          swiperConfig.scrollbar = {
            el: scrollbarElement, // this section's own bar; the bare selector can match another section's first
            draggable: true,
            dragSize: 'auto',
          };
        }

        // Initialize Swiper
        try {
          this.swiperInstance = new Swiper(swiperElement, swiperConfig);
        } catch (error) {
          console.error('Featured Collection: Swiper initialization failed', error);
        }
      }

      destroySwiper() {
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }
      }
    }

    customElements.define('featured-collection-carousel', FeaturedCollectionCarousel);
  }



  



/*
  Yarn winding, 2026-11-03. Orders used to say "Yarn Winding Service x2" with nothing tying that to a yarn, so on an
  order with several colours nobody could tell which to wind, and raising the yarn quantity in the cart left the
  winding count behind.

  - Ticking the box now labels both lines. The yarn gets "Wind into cakes: Yes", the fee gets "For: <yarn> - <colour>",
    and both carry the hidden _pf_wind link (the yarn's variant id). Each colour gets its own fee line. Labels show in
    the cart, checkout, confirmation email and admin, and land in SEOM's Order Details Option01..10.
  - Winding one colour twice merges as usual, since the variant and the labels match. Wanting 1 of 4 wound means
    adding 1 ticked and 3 unticked, which stay separate lines.
  - A ticked add now behaves like an unticked one: it stays on the page and opens the cart drawer, so the shopper can
    pick the next colour with the box still ticked. It used to jump to the /cart page.
  - pfWindingSync below keeps each fee line equal to its yarn line after that.
*/
if (!customElements.get('winding-service-block')) {
  class WindingServiceBlock extends HTMLElement {
    constructor() {
      super();
      this.pricePerWinding = 2.00;
      this.windingProduct = null;
      this.windingServiceVariantId = null;
      this.windingServicePrice = null;
      this.adding = false;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.init();
    }

    init() {
      this.loadWindingProductData();

      if (!this.windingProduct || !this.windingServiceVariantId) {
        console.error('Winding service product not found or invalid');
        return;
      }

      this.windingCheckbox = this.querySelector('#add-winding-service');
      this.windingDetails = this.querySelector('.winding-details');
      this.windingTotalCost = this.querySelector('#winding-total-cost');
      this.windingCount = this.querySelector('#winding-count');

      const infoWrapper = this.closest('.product__info-wrapper');
      this.quantityInput = infoWrapper?.querySelector('input[name="quantity"]');
      this.addToCartButton = infoWrapper?.querySelector('form[action="/cart/add"] [type="submit"]');

      if (!this.windingCheckbox || !this.quantityInput || !this.addToCartButton) {
        console.error('Missing required elements for winding service');
        return;
      }

      if (this.windingServicePrice) this.pricePerWinding = this.windingServicePrice;

      this.bindEvents();
      this.updateWindingUI();
    }

    loadWindingProductData() {
      const scriptTag = document.querySelector('script[data-winding-product]');

      if (!scriptTag) {
        console.error('Winding product script tag not found');
        return;
      }

      try {
        this.windingProduct = JSON.parse(scriptTag.textContent);

        if (this.windingProduct && this.windingProduct.available) {
          this.windingServiceVariantId = this.windingProduct.variants[0].id;
          this.windingServicePrice = this.windingProduct.price / 100;
        } else {
          console.error('Invalid winding product data structure');
        }
      } catch (error) {
        console.error('Error parsing winding product JSON:', error);
      }
    }

    bindEvents() {
      this.windingCheckbox.addEventListener('change', () => {
        this.updateWindingUI();
      });

      this.quantityInput.addEventListener('input', () => {
        this.updateWindingCost();
      });

      this.quantityInput.addEventListener('change', () => {
        this.updateWindingCost();
      });

      this.addToCartButton.addEventListener('click', (e) => {
        if (!this.windingCheckbox.checked || !this.isOffered()) return;
        if (this.addToCartButton.getAttribute('aria-disabled') === 'true') return;
        e.preventDefault();
        this.addToCartWithWindingService();
      });
    }

    // Hidden by put_up (snippets/backorder-banner.liquid) on anything that is not a hank. A browser can restore a
    // ticked box on such a page, and that must never add a charge.
    isOffered() {
      return getComputedStyle(this).display !== 'none';
    }

    updateWindingUI() {
      const isChecked = this.windingCheckbox.checked;
      this.windingDetails.classList.toggle('open-state', isChecked);

      const checkboxSvg = this.windingCheckbox.parentElement.querySelector('.custom-checkbox svg');
      if (checkboxSvg) checkboxSvg.style.display = isChecked ? 'block' : 'none';

      this.updateWindingCost();
    }

    updateWindingCost() {
      const quantity = parseInt(this.quantityInput.value, 10) || 1;
      const windingCost = this.windingCheckbox.checked ? quantity * this.pricePerWinding : 0;
      this.windingTotalCost.textContent = windingCost.toFixed(2);
      if (this.windingCount) this.windingCount.textContent = quantity === 1 ? '1 hank' : quantity + ' hanks';
    }

    // The selected colour's name, for the fee line. The page keeps it current as colours change.
    async selectedVariantTitle(variantId) {
      const scope = this.closest('.product__info-wrapper') || document;
      try {
        const selected = JSON.parse(scope.querySelector('[data-selected-variant]').textContent);
        if (String(selected.id) === String(variantId)) return selected.title;
      } catch (error) {}
      try {
        const response = await fetch(`/variants/${variantId}.js`);
        return (await response.json()).title || '';
      } catch (error) {
        return '';
      }
    }

    setLoading(isLoading) {
      const button = this.addToCartButton;
      button.classList.toggle('loading', isLoading);
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      if (isLoading) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    async addToCartWithWindingService() {
      if (this.adding) return;
      this.adding = true;

      const form = this.addToCartButton.closest('form');
      const productForm = this.addToCartButton.closest('product-form');
      const quantity = parseInt(this.quantityInput.value, 10) || 1;
      const yarnVariantId = String(form.querySelector('[name="id"]').value);

      productForm?.handleErrorMessage?.();
      this.setLoading(true);

      try {
        const variantTitle = await this.selectedVariantTitle(yarnVariantId);
        const yarnTitle = this.dataset.yarnTitle || '';
        const forLabel = variantTitle && variantTitle !== 'Default Title' ? `${yarnTitle} - ${variantTitle}` : yarnTitle;

        // Anything else the form sends for this line (properties[...]) still goes with the yarn.
        const formProperties = {};
        new FormData(form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value) formProperties[match[1]] = value;
        });

        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: parseInt(yarnVariantId, 10),
                quantity,
                properties: { 'Wind into cakes': 'Yes', ...formProperties, _pf_wind: yarnVariantId },
              },
              {
                id: parseInt(this.windingServiceVariantId, 10),
                quantity,
                properties: { For: forLabel, _pf_wind: yarnVariantId },
              },
            ],
          }),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          // More than we can sell: Shopify still adds what it has and takes the fee at the full count, then answers
          // 422 ("Only 10 items were added"). Show that, and let the sync bring the fee down to the yarn.
          productForm?.handleErrorMessage?.(result.description || result.message || window.cartStrings?.error);
          window.pfWindingSync?.run();
          return;
        }

        const smartCart = window.Rebuy?.SmartCart;
        if (smartCart && typeof smartCart.show === 'function') smartCart.show();
        else window.location.href = window.routes?.cart_url || '/cart';
      } catch (error) {
        console.error('Error adding yarn with winding:', error);
        productForm?.handleErrorMessage?.(window.cartStrings?.error);
      } finally {
        this.adding = false;
        this.setLoading(false);
      }
    }
  }

  customElements.define('winding-service-block', WindingServiceBlock);
}

/*
  Keeps every labelled winding fee equal to the yarn it belongs to, in the Rebuy drawer and on the /cart page.
  - Yarn quantity changes: the fee follows.
  - Yarn removed: the fee goes with it.
  - Fee removed: the "Wind into cakes" label comes off the yarn, so the packer does not wind something unpaid.
  Fee lines with no _pf_wind link (Rebuy's cart offer, carts from before this) are left alone.

  It watches network timing for cart writes instead of wrapping fetch, so it sees writes from Rebuy, Redo and the theme
  alike and sits on nobody's request path. Rebuy has its own fetch watcher, so these writes refresh the drawer.
  No cart read happens on page load except on /cart: only after a cart write or when the drawer opens.
  Carts with no winding are never dimmed or delayed.
*/
(function () {
  if (window.pfWindingSync) return;

  const FEE_HANDLE = 'yarn-winding-service';
  const LINK = '_pf_wind';
  const LABEL = 'Wind into cakes';
  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const MAX_CHANGES = 12;
  const CHECKOUT_WAIT_MS = 2500;
  const LOCK_MAX_MS = 6000;
  const WRITE_WAIT_MS = 1500;
  const LOCK_CLASS = 'pf-winding-syncing';
  const WINDING_LINE_CONTROL = [
    '.rebuy-cart__flyout-item.property-key-wind-into-cakes button',
    '.rebuy-cart__flyout-item.property-key-wind-into-cakes input',
    '.rebuy-cart__flyout-item.product-yarn-winding-service.property-key-for button',
  ].join(', ');

  let timer = null;
  let running = null;
  let rerun = false;
  let cartHasLinks = false;
  let passThrough = false;
  let lockRelease = null;
  let lastControlAt = 0;
  let lastWriteEnd = 0;

  const root = () => window.Shopify?.routes?.root || '/';
  const linkOf = (item) => item.properties && item.properties[LINK];

  function nextChange(cart) {
    const items = cart.items || [];
    const fees = items.filter((item) => item.handle === FEE_HANDLE && linkOf(item));
    const yarns = items.filter((item) => item.handle !== FEE_HANDLE && linkOf(item));
    const paidFor = new Set();

    for (const fee of fees) {
      const link = linkOf(fee);
      const wanted = paidFor.has(link)
        ? 0
        : yarns.filter((yarn) => linkOf(yarn) === link).reduce((total, yarn) => total + yarn.quantity, 0);
      paidFor.add(link);
      if (fee.quantity !== wanted) return { update: { id: fee.key, quantity: wanted } };
    }

    for (const yarn of yarns) {
      if (!paidFor.has(linkOf(yarn))) {
        // change.js ignores an empty property list and keeps blank keys on the line, so swap the line for a clean one.
        // It merges into any plain line of the same colour, which is the right result.
        const properties = { ...yarn.properties };
        delete properties[LINK];
        delete properties[LABEL];
        return {
          update: { id: yarn.key, quantity: 0 },
          add: { id: yarn.variant_id, quantity: yarn.quantity, properties },
        };
      }
    }

    return null;
  }

  const post = (path, body) =>
    fetch(`${root()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  async function sync() {
    let cart = await (await fetch(`${root()}cart.js`)).json();
    let changed = false;

    for (let i = 0; i < MAX_CHANGES; i++) {
      const change = nextChange(cart);
      if (!change) break;
      const response = await post('cart/change.js', change.update);
      if (!response.ok) break;
      cart = await response.json();
      changed = true;
      if (change.add) {
        await post('cart/add.js', { items: [change.add] });
        cart = await (await fetch(`${root()}cart.js`)).json();
      }
    }

    cartHasLinks = (cart.items || []).some(linkOf);
    if (changed && /\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  }

  function run() {
    clearTimeout(timer);
    timer = null;
    if (running) {
      rerun = true;
      return running;
    }
    running = sync()
      .catch((error) => console.error('Winding sync:', error))
      .finally(() => {
        running = null;
        if (rerun) {
          rerun = false;
          schedule(0);
        }
      });
    return running;
  }

  function schedule(delay = 350) {
    clearTimeout(timer);
    timer = setTimeout(run, delay);
  }

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        const writes = list.getEntries().filter((entry) => CART_WRITE.test(entry.name));
        if (!writes.length) return;
        lastWriteEnd = Math.max(lastWriteEnd, ...writes.map((entry) => entry.responseEnd));
        schedule();
      }).observe({ type: 'resource' });
    } catch (error) {}
  }

  document.addEventListener('rebuy:smartcart.show', () => schedule(0));
  if (/\/cart\/?$/.test(window.location.pathname)) schedule(0);

  // Drawer: while a winding line is changing, its checkout buttons are dimmed and can't be pressed (base.css). They can
  // beat the sync otherwise: Redo's Checkout+ lives in a closed shadow root, so a click on it can't be held and replayed,
  // and a raise followed by a click within about two seconds left the yarn at 2 and the fee at 1.
  const rebuyQuiet = () =>
    new Promise((resolve) => {
      const rebuyCart = window.Rebuy?.Cart;
      if (rebuyCart?.whenCartQuiescent) rebuyCart.whenCartQuiescent(resolve);
      else setTimeout(resolve, 800);
    });

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function settle() {
    let handled;
    do {
      handled = lastControlAt;
      // Rebuy debounces quantity clicks, so nothing may be on the wire yet. Wait for its write to land. A click it
      // refuses (already at the stock limit) never writes, hence the limit.
      while (lastWriteEnd < handled && performance.now() < handled + WRITE_WAIT_MS) await wait(40);
      await rebuyQuiet();
      // A sync already under way may have read the cart before that write landed, so the one that counts starts now.
      // The dim lifts as soon as it finishes; the no-op check its own write triggers doesn't hold anything up.
      while (running) await running;
      await run();
    } while (lastControlAt !== handled);
  }

  function lockCheckout() {
    lastControlAt = performance.now();
    if (lockRelease) return;
    const page = document.documentElement;
    page.classList.add(LOCK_CLASS);
    const failsafe = setTimeout(() => lockRelease?.(), LOCK_MAX_MS);
    lockRelease = () => {
      clearTimeout(failsafe);
      page.classList.remove(LOCK_CLASS);
      lockRelease = null;
    };
    settle().finally(() => lockRelease?.());
  }

  ['click', 'change'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (event.target.closest?.(WINDING_LINE_CONTROL)) lockCheckout();
      },
      true
    );
  });

  // /cart page: its checkout button is ordinary, so hold the click until the sync is done and press it again.
  document.addEventListener(
    'click',
    (event) => {
      if (passThrough) {
        passThrough = false;
        return;
      }
      if (!timer && !running && !cartHasLinks) return;
      const button = event.target.closest?.('.rebuy-cart__checkout-button, button[name="checkout"], input[name="checkout"]');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      Promise.race([run(), new Promise((resolve) => setTimeout(resolve, CHECKOUT_WAIT_MS))]).then(() => {
        passThrough = true;
        button.click();
      });
    },
    true
  );

  window.pfWindingSync = { run, nextChange };
})();